/* eslint-disable no-shadow */
import moment from 'moment';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import _, {
  find,
  groupBy,
  values,
  flatten,
  difference,
  add,
  curry,
  flow,
  partial,
  map,
  filter,
} from 'lodash';
import { formatAmount } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { unwind } from '@clubmed/utils';
import { getBookingPrecision } from '@clubmed/domains-service';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
type TreeOptions = {
  filtered?: boolean;
};
type A = Readonly<{
  id: string;
  birthdate: string;
  firstName?: string;
  isChild?: boolean;
  lastName?: string;
  unicodeFirstName?: string;
  unicodeLastName?: string;
}>;
const AGE_OF_MAJORITY = 18;

const isAdult = (birthday) => {
  return !moment(birthday, API_DATE_FORMAT).isAfter(
    moment().startOf('day').subtract(AGE_OF_MAJORITY, 'years'),
  );
};

export const TOAST_DURATION = 4000;
export const getAdditionalServiceFromUpsellId = <T extends UpsellRental | UpsellActivity>(
  id: ActivityId | RentalId,
  availableUpsellsUpdatedWithCart: ReadonlyArray<T> = [],
): T | null => {
  return availableUpsellsUpdatedWithCart.find((a) => a.pricingInfo.id === id) || null;
};
export function getSmallerPrice<T extends PriceType | FormattedPrice>(
  prev: T | null | undefined,
  next: T | null | undefined,
): T | null | undefined {
  if (!prev?.value) {
    return next;
  }

  if (next?.value) {
    return next.value < prev.value ? next : prev;
  }
}

const mergeActivitiesCategoriesWithAdditionnalServices = (
  cat: ActivitiesCategoryEntity,
  availableActivities: ReadonlyArray<UpsellActivity>,
  options?: TreeOptions,
) => {
  if (cat.categories?.length) {
    const category = {
      ...cat,
      categories: cat.categories
        .map<ActivitiesCategoryEntity>((subcat) =>
          mergeActivitiesCategoriesWithAdditionnalServices(subcat, availableActivities, options),
        )
        .filter((subcat) => !!subcat.categories?.length || !!subcat.activities?.length),
    };
    return {
      ...category,
      bestPrice: category.categories?.reduce(
        (acc, { bestPrice }) => getSmallerPrice<PriceType>(acc, bestPrice),
        null,
      ),
      onlineBookableCount: category.categories?.reduce(
        (acc, { onlineBookableCount }) => acc + onlineBookableCount,
        0,
      ),
    };
  }

  if (cat.activities?.length) {
    const mergedActivities = (cat.activities || [])
      .map<ActivityEntity>((a) => ({
        ...a,
        additionalService: getAdditionalServiceFromUpsellId<UpsellActivity>(
          a.id,
          availableActivities,
        ),
      })) // filter depending on additionalService (nullable)
      // then merge activities received from resort and the result of '/additional_services'
      .filter((a) => (options?.filtered ? !!a.additionalService : true)) // and display online bookable activities first
      .sort(
        (activity1, activity2) =>
          Number(!!activity2.additionalService) - Number(!!activity1.additionalService),
      );
    const onlineBookableActivities = mergedActivities.filter(
      ({ additionalService }) => !!additionalService,
    );
    const mergedActivitiesWithPrice = onlineBookableActivities.filter(
      ({ additionalService }) =>
        Boolean(additionalService?.pricingInfo?.startingPricePerAdult) &&
        typeof additionalService?.pricingInfo?.startingPricePerAdult?.value === 'number',
    );
    return {
      ...cat,
      activities: mergedActivities,
      bestPrice: mergedActivitiesWithPrice.length
        ? mergedActivitiesWithPrice.reduce<PriceType | null>(
            (acc, { additionalService }) =>
              getSmallerPrice<PriceType>(
                acc,
                additionalService?.pricingInfo?.startingPricePerAdult as unknown as
                  | PriceType
                  | null
                  | undefined,
              ) as PriceType | null,
            null,
          )
        : null,
      onlineBookableCount: onlineBookableActivities.length,
    };
  }

  return cat;
};

export const mapActivitiesCategoriesWithAdditionnalServices = (
  categories: Array<ActivitiesCategoryEntity>,
  availableActivities: ReadonlyArray<UpsellActivity>,
  options?: TreeOptions,
) =>
  (categories || []).map<ActivitiesCategoryEntity>((category) =>
    mergeActivitiesCategoriesWithAdditionnalServices(category, availableActivities, options),
  );
export const mapRentalsWithAdditionnalServices = (
  rentals: RentalsTree,
  availableRentals: ReadonlyArray<UpsellRental>,
  options?: TreeOptions,
) =>
  rentals.map<RentalTreeUseCase>((useCase) => ({
    ...useCase,
    items: useCase.items
      .map((type) => ({
        ...type,
        items: type.items
          .map((rental) => ({
            ...rental,
            additionalService: getAdditionalServiceFromUpsellId<UpsellRental>(
              rental.id,
              availableRentals,
            ),
          }))
          .filter((r) => (options?.filtered ? r.additionalService : true)),
      }))
      .filter((uc) => !!uc.items.length),
  }));
export const getAttendeesFromCart = (cart: Cart) =>
  (cart?.attendees || []).map<A>(
    ({ birthday, firstname, id, lastname, unicodeFirstname, unicodeLastname }) => ({
      birthdate: birthday,
      firstName: firstname,
      id,
      lastName: lastname,
      unicodeFirstName: unicodeFirstname,
      unicodeLastName: unicodeLastname,
    }),
  );

const updatePrice = (obj, price, currency, locale) => {
  const value = obj.value + price;
  return { ...obj, value, formattedValue: formatAmount(value, currency, locale) };
};

const compose2 = (g, f) => (x) => g(f(x));

const deepClone = compose2(JSON.parse, JSON.stringify);

function mapCartAttendeeIdToPricingInfoId(
  cartAttendeeId: string,
  pricingInfo: { attendees?: ReadonlyArray<{ id: string; birthdate?: string }> },
  kidAttendees: ReadonlyArray<{ id: string; birthdate?: string }> = [],
): string {
  if (pricingInfo.attendees?.some(({ id }) => id === cartAttendeeId)) {
    return cartAttendeeId;
  }

  const kid = kidAttendees.find(({ id }) => id === cartAttendeeId);

  if (!kid?.birthdate) {
    return cartAttendeeId;
  }

  return (
    pricingInfo.attendees?.find(({ birthdate }) => birthdate === kid.birthdate)?.id ??
    cartAttendeeId
  );
}

export const normalizeCartChildcaresForChildcarePricingInfo = (
  cartChildcares: ReadonlyArray<CartRental | CartChildcare>,
  pricingInfo: { attendees?: ReadonlyArray<{ id: string; birthdate?: string }> },
  kidAttendees: ReadonlyArray<{ id: string; birthdate?: string }> = [],
): ReadonlyArray<CartRental | CartChildcare> => {
  const remapAttendeeId = (cartAttendeeId: string) =>
    mapCartAttendeeIdToPricingInfoId(cartAttendeeId, pricingInfo, kidAttendees);

  return cartChildcares.map((cartChildcare) => ({
    ...cartChildcare,
    attendees: cartChildcare.attendees?.map((attendee) => ({
      ...attendee,
      id: remapAttendeeId(attendee.id),
    })),
    schedules: cartChildcare.schedules?.map((schedule) => ({
      ...schedule,
      attendees: schedule.attendees?.map((attendee) => ({
        ...attendee,
        id: remapAttendeeId(attendee.id),
      })),
    })),
  }));
};

function doesCartEntryMatchChildcareSlot(
  cardEntry: {
    slotId: string;
    startDate: string;
    endDate: string;
    productId?: string;
  },
  schedule: { startDate: string; endDate: string; timeSlots?: ReadonlyArray<{ id: string }> },
  slot: { id: string },
  pricingInfo: { type?: string; id?: string; progId?: string; ids?: string[] },
): boolean {
  if (cardEntry.startDate !== schedule.startDate || cardEntry.endDate !== schedule.endDate) {
    return false;
  }

  if (cardEntry.slotId === slot.id) {
    return true;
  }

  if (pricingInfo.type !== 'CHILDCARE') {
    return false;
  }

  // Single-slot childcare (e.g. Baby Club): cart may use product/prog id instead of slot id.
  if ((schedule.timeSlots || []).length !== 1) {
    return false;
  }

  const childcareIds = [
    pricingInfo.id,
    pricingInfo.progId,
    ...(pricingInfo.ids || []),
    cardEntry.productId,
  ].filter(Boolean) as string[];

  return (
    childcareIds.includes(cardEntry.slotId) ||
    (!!cardEntry.productId && childcareIds.includes(cardEntry.productId))
  );
}

// The updatePriceInfo function updates the pricingInfo of one activity get from the activity tree
// and the booking cart in order to give the GM a correct feedback of what he can or has, already or not rented
// The activity tree containes all the activities that can be ranted online, ordered and classed by categories.
// It's build from the v0/additional_services API endpoint by the graphql server. The goal of the data
// transformations made by the graphql endpoint is to deliver to the front, for each activity, a data structure
// that fits well into the ReservationBlock UI form.
export const updatePriceInfo = (
  pInfo: any,
  cActivities: ReadonlyArray<CartRental | CartChildcare>,
  locale: string,
) => {
  if (!pInfo || !cActivities) {
    return pInfo;
  }

  // pricingInfo should be unmutable since updatePriceInfo could be called multiple times
  const pricingInfo = deepClone(pInfo);
  const cartActivities = deepClone(cActivities);
  // we explode the cart structure to update easyly the pricingInfo
  // we use implepmented the unwind function of the mongodb aggregate framework
  // In that example, unwinding on 'size' turns
  // { id: 1, item: 'ABC1', sizes: [ 'S', 'M', 'L'] }
  // into
  // [
  //   { id : 1, item : ABC1, sizes : 'S' }
  //   { id : 1, item : ABC1, sizes : 'M' }
  //   { id : 1, item : ABC1, sizes : 'L' }
  // ]
  const cardRawTable = unwind(unwind(cartActivities, 'schedules'), 'schedules.attendees').map(
    ({ id, schedules, ...others }) => {
      const { startDate, endDate } = schedules;
      const { id: attendeeId, price } = schedules.attendees;
      return { ...others, attendeeId, price, startDate, endDate, slotId: id };
    },
  );
  // we update the pricingInfo with the content of the cart
  pricingInfo.attendees.forEach((attendee) => {
    attendee.schedules.forEach((schedule) => {
      schedule.timeSlots &&
        schedule.timeSlots.forEach((slot) => {
          const foundSlot = find(cardRawTable, (cardEntry) => {
            if (cardEntry.attendeeId !== attendee.id) {
              return false;
            }

            return doesCartEntryMatchChildcareSlot(cardEntry, schedule, slot, pricingInfo);
          });
          const isRented = !!foundSlot;
          slot.isRented = isRented;

          if (isRented) {
            pricingInfo.isPartiallyRented = true;
            schedule.isPartiallyRented = true;
            attendee.rentedAmount = updatePrice(
              attendee.rentedAmount,
              foundSlot?.price,
              pricingInfo.currency,
              locale,
            );
            schedule.rentedAmount = updatePrice(
              schedule.rentedAmount,
              foundSlot?.price,
              pricingInfo.currency,
              locale,
            );
            pricingInfo.rentedAmount = updatePrice(
              pricingInfo.rentedAmount,
              foundSlot?.price,
              pricingInfo.currency,
              locale,
            );

            if (pricingInfo.type === 'CHILDCARE') {
              pricingInfo.schedules?.forEach((pricingSchedule) => {
                if (
                  pricingSchedule.startDate !== schedule.startDate ||
                  pricingSchedule.endDate !== schedule.endDate
                ) {
                  return;
                }

                pricingSchedule.slots?.forEach((pricingSlot) => {
                  if (pricingSlot.id !== slot.id) {
                    return;
                  }

                  pricingSlot.attendees?.forEach((pricingAttendee) => {
                    if (pricingAttendee.id === attendee.id) {
                      pricingAttendee.isRented = true;
                      pricingAttendee.isInCart = true;
                    }
                  });
                });
              });
            }
          }
        });

      if (schedule.timeSlots && schedule.timeSlots.every(({ isRented }) => isRented)) {
        schedule.isFullyRented = true;
      }
    });
    pricingInfo.isFullyRented = pricingInfo.attendees.every((attendee) =>
      attendee.schedules.every(({ isFullyRented }) => isFullyRented),
    );
  });
  return pricingInfo;
};

const filterAvailableServices = ({ availableActivities, availableRentals, paymentStatus }) => {
  return availableActivities
    .concat(availableRentals)
    .filter((activity) =>
      paymentStatus === 'isPaid'
        ? activity.pricingInfo.isPartiallyPaid || activity.pricingInfo.isFullyPaid
        : activity.pricingInfo.isPartiallyRented || activity.pricingInfo.isFullyRented,
    );
};

const gatherPrimaryKeys = ({ pricingInfo, productInfo }) => {
  const { attendees, schedules: drop, ...othersP } = pricingInfo;
  const { id, schedules, ...othersA } = attendees;
  const { startDate, endDate, timeSlots: timeSlot, ...othersS } = schedules;
  return {
    attendeeId: attendees.id,
    startDate,
    endDate,
    slotId: timeSlot.id,
    attendee: {
      id,
      ...othersA,
    },
    schedule: {
      startDate,
      endDate,
      ...othersS,
    },
    timeSlot,
    pricingInfo: { ...othersP },
    productInfo,
  };
};

const explodeToJoinTable = flow(
  partial(curry(unwind), _, 'pricingInfo.attendees'),
  partial(curry(unwind), _, 'pricingInfo.attendees.schedules'),
  partial(curry(unwind), _, 'pricingInfo.attendees.schedules.timeSlots'),
  partial(curry(map), _, gatherPrimaryKeys),
);

const reaggragateRentedServicesOverJoinTable = (
  pricingInfoJoinTable,
  locale,
  attendee,
  paymentStatus,
) =>
  values(groupBy(pricingInfoJoinTable, 'pricingInfo.id')).map((rentedService) => {
    const services = values(groupBy(rentedService, 'slotId'))
      .filter((entries) => {
        return entries.some(
          (entry) => entry.attendeeId === attendee.id && entry.timeSlot[paymentStatus],
        );
      })
      .map((entries) => {
        const rentedAmountValue = entries
          .map(({ timeSlot }) => timeSlot)
          .filter((timeSlot) => timeSlot[paymentStatus])
          .map(({ price }) => price.value)
          .reduce(add, 0);
        return {
          pricingInfo: {
            id: entries[0].slotId,
            productId: entries[0].pricingInfo.id,
            timeSlot: entries[0].timeSlot.slotName,
            type: entries[0].pricingInfo.type,
            rentedAmount: {
              value: rentedAmountValue,
              formattedValue: formatAmount(
                rentedAmountValue,
                entries[0].pricingInfo.currency,
                locale,
              ),
              currency: entries[0].pricingInfo.currency,
            },
            startingPricePerAdult: entries[0].pricingInfo.startingPricePerAdult,
            isPartiallyRented: true,
            isPartiallyPaid:
              entries.map(({ timeSlot }) => timeSlot).filter(({ isPaid }) => isPaid).length > 0,
            isFullyPaid:
              entries.map(({ timeSlot }) => timeSlot).filter(({ isPaid }) => isPaid).length ===
              entries.length,
            schedules: values(groupBy(entries, 'startDate')).map((entries) => ({
              startDate: entries[0].startDate,
              endDate: entries[0].endDate,
              attendees: entries.map((entry) => ({ ...entry.timeSlot, id: entry.attendeeId })),
            })),
          },
          productInfo: entries[0].productInfo,
        };
      });
    return {
      price: flatten(
        flatten(services.map((service) => service.pricingInfo.schedules)).map(
          ({ attendees }) => attendees,
        ),
      )
        .filter((entry) => entry.id === attendee.id && entry[paymentStatus])
        .map(({ price }) => price.value)
        .reduce(add, 0),
      services,
    };
  });

// In order to send to the CartComponent the data turned into the right format, we have to reverse
// the strcuture of it. We do this by exploding it and reaggregating over it. It'as a generic way to
// make that kind of transformation.
const transformToCardFormat = ({
  attendees,
  locale,
  paymentStatus,
  availableActivities,
  availableRentals,
}) =>
  map(attendees, (attendee, index) => {
    const { id, firstname, lastname, unicodeLastname, unicodeFirstname, birthday } = attendee;
    return {
      id,
      firstName: firstname,
      lastName: lastname,
      unicodeLastName: unicodeLastname,
      unicodeFirstName: unicodeFirstname,
      isAdult: isAdult(birthday),
      index: index + 1,
      rentedServices: flow(
        filterAvailableServices,
        explodeToJoinTable,
        partial(curry(reaggragateRentedServicesOverJoinTable), _, locale, attendee, paymentStatus),
      )({
        availableActivities: availableActivities,
        availableRentals: availableRentals,
        paymentStatus,
      }),
    };
  });

const keepOnlyAttendeesWithRentedServices = (attendee) =>
  attendee.rentedServices.some(({ services }) => services.length > 0);

const cleanRentedServices = (attendee) => ({
  ...attendee,
  rentedServices: attendee.rentedServices.filter(({ services }) => services.length > 0),
});

// The BIG function that make the structure upside down. Turns pricingInfo strcture to the Cart one.
export const getReservedServicesByAttendee = ({
  attendees,
  availableActivities = [],
  availableRentals = [],
  locale,
  paymentStatus,
}: any): Array<any> =>
  flow(
    transformToCardFormat,
    partial(curry(filter), _, keepOnlyAttendeesWithRentedServices),
    partial(curry(map), _, cleanRentedServices),
  )({
    attendees,
    locale,
    paymentStatus,
    availableActivities: deepClone(availableActivities),
    availableRentals: deepClone(availableRentals),
  });
export const excludePaidSlots = (updatedService: any, activities: any): any =>
  updatedService
    .map((attendee) => ({
      ...attendee,
      schedules: attendee.schedules
        .map((schedule) => ({
          ...schedule,
          slots: schedule.slots.filter(
            (slotId) =>
              !activities
                .find((activity) => activity.pricingInfo.id === attendee.productInfoId)
                .pricingInfo.attendees.find((att) => att.id === attendee.customerId)
                .schedules.find(
                  (sched) =>
                    sched.startDate === schedule.startDate && sched.endDate === schedule.endDate,
                )
                .timeSlots.find((slot) => slot.id === slotId).isPaid,
          ),
        }))
        .filter((schedule) => schedule.slots.length !== 0),
    }))
    .filter((attendee) => attendee.schedules.length !== 0);

export const getRemovedAttendeesFromServiceUpdate = (
  pricingInfo: PricingInfoWithScheduleType,
  submittedAttendees: ServiceMutationType,
): { attendeeIds: string[]; serviceIds: string[] } =>
  pricingInfo.attendees
    .filter(
      ({ id, schedules }) =>
        !submittedAttendees.some(({ customerId }) => customerId === id) &&
        schedules.some(
          ({ isPartiallyRented, isPartiallyPaid }) => isPartiallyRented || isPartiallyPaid,
        ),
    )
    .reduce(
      (acc, { id, schedules }) => ({
        attendeeIds: [...acc.attendeeIds, id],
        serviceIds: [
          ...acc.serviceIds,
          ...schedules.flatMap((schedule) =>
            schedule.timeSlots
              .filter(({ isRented, isPaid }) => isRented || isPaid)
              .map((timeSlot) => timeSlot.id),
          ),
        ],
      }),
      { attendeeIds: [] as string[], serviceIds: [] as string[] },
    );

// The same technic is used here to reverse the strcture shape: explode (unwind) and reaggregate (groupBy)
// This transformation resolves to the POST /v0/customers/{customer_id}/bookings/{booking_id}/cart/services payload format
export const formatServiceToUpdateBookingCart = ({
  appContext,
  bookingId,
  serviceIds,
  type,
  updatedService,
  stayIndex,
}: any): any => {
  let table = unwind(updatedService, 'schedules');
  table = unwind(table, 'schedules.slots');
  table = table.map(({ customerId: attendeeId, schedules, productInfoId }) => {
    const { slots, startDate, endDate } = schedules;
    return {
      attendeeId,
      productInfoId,
      startDate,
      endDate,
      slotId: slots,
    };
  });
  const card = values(groupBy(table, 'slotId')).map((entries) => {
    const schedules = values(groupBy(entries, 'startDate')).map((entries) => ({
      start_date: entries[0].startDate,
      end_date: entries[0].endDate,
      attendees: entries.map((entry) => ({
        id: entry.attendeeId,
      })),
    }));
    return {
      id: entries[0].slotId,
      action: 'update',
      context: appContext,
      bookingId: bookingId,
      serviceName: type,
      schedules,
      stayIndex,
    };
  });
  const diff = difference(
    serviceIds,
    card.map(({ id }) => id),
  );

  if (diff.length) {
    diff.forEach((id) => {
      card.push({
        id,
        action: 'remove',
        context: appContext,
        bookingId: bookingId,
        serviceName: type,
        schedules: [],
        stayIndex,
      });
    });
  }

  return card;
};
export const servicesWithSchedulePricingInfoQuery = `
pricingInfo {
        id
        ids
        progId
        childId
        type
        currency
        rentedAmount {...priceDetails, currency}
        startingPricePerAdult {
          ...priceDetails
          currency
          discount
          discountUnit
          formattedInitialValue
        }
        isFullyPaid
        isPartiallyRented
        isPartiallyPaid
        attendees {
          id
          rentedAmount {...priceDetails}
          price {
            currency
            discount
            discountUnit
            formattedInitialValue
            formattedValue(precision:0)
            value
          }
          schedules {
            startDate
            endDate
            isPartiallyRented
            isPartiallyPaid
            isFullyRented
            isFullyPaid
            rentedAmount {...priceDetails}
            timeSlots {
              id
              slotName
              remainingStock
              isRented
              isPaid
              price {
                ...priceDetails
                currency
                discount
                discountUnit
                formattedInitialValue
              }
            }
          }
        }
        schedules {
          startDate
          endDate
          slots {
            id
            type
            initialStock
            remainingStock
            attendees {
              id
              price {
                ...priceDetails
              }
            }
          }
        }
      }`;
export const childcaresQuery = `
childcares {
  ${servicesWithSchedulePricingInfoQuery}
  productInfo {
    id
    label
    images
    description
    durationDescription
    isIncluded
    ageInMonths {
      min
      max
    }
    openingHours {
      start
      end
    }
    timeSlots {
      type
      opening
      closing
    }
    closingWeekdays
  }
}`;
export const transfersQuery = (appContext: AppContext) => `
      transfers {
        pricingInfo {
          id
          ids
          isFullyPaid
          isFullyRented
          isIncluded
          isPartiallyPaid
          isPartiallyRented
          price: startingPricePerAdult {
            ...priceDetails
            formattedValueRounded : formattedValue(precision : 0)
          }
          totalPrice {
            value
            formattedValue(precision: ${getBookingPrecision(appContext.locale)})
            currency
          }
          rentedAmount {
            value
          }
          schedules {
            startDate
            endDate
            remainingStock
            attendees {
              id
            }
          }
          type
        }
        productInfo {
          accessId
          description
          durationInMinutes
          id
          label
          vehicle {
            id
            label
          }
          waitingInMinutes
        }
        directionType
      }
      `;
export const whiteLabelServicesQuery = (appContext: AppContext) => `whiteLabelServices {
        pricingInfo {
          id
          ids
          type
          totalPrice {
            value
            formattedValue(precision: ${getBookingPrecision(appContext.locale)})
            currency
          }
          startingPricePerAdult {
            value
            formattedValue(precision: ${getBookingPrecision(appContext.locale)})
            currency
          }
          isPartiallyRented
          isPartiallyPaid
          isFullyPaid
          schedules {
            startDate
            endDate
            remainingStock
            isPartiallyPaid
            isFullyPaid
            attendees {
              id
            }
          }
        }
        productInfo {
          id
          image
          type
          title
          description
        }
      }
      `;
export const insuranceQuery = (appContext: AppContext) => `insurance {
        pricingInfo {
          id
          type
          totalPrice {
            value
            formattedValue(precision: ${getBookingPrecision(appContext.locale)})
            currency
          }
          isPartiallyRented
          isPartiallyPaid
          isFullyPaid
          schedules {
            startDate
            endDate
            attendees {
              id
            }
          }
        }
        productInfo {
          id
          label
          shortDescription
          insuranceDetails {
            id
            label
            images
            description
            shortDescription
            videos
            attachedDocuments {
              label
              url
            }
            company {
              label
              logo
            }
            coverageDescriptions
            subscriptionConditions {
              age_min
              ageMax
              byAttendees
            }
          }
        }
      }`;
